import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@safetyhub/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { useModalStore } from '../../stores/modal';
import { extractApiError } from '../../lib/api';
import { resolveWithEvidence, uploadResolutionPhoto } from '../../lib/inspections';
import Modal from '../Modal';
import { useToastStore } from '../../stores/toast';

const MAX_PHOTOS = 5;

/**
 * 푸터의 제출 버튼이 본문의 폼을 가리키는 `form` 속성 값. base Modal이 본문과 푸터를 형제
 * div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 */
const FORM_ID = 'resolution-photo-form';

interface PhotoPreview {
  file: File;
  previewUrl: string;
}

interface ResolutionPhotoModalProps {
  /** 성공 후 호출되는 콜백 */
  onSuccess?: () => void;
}

export default function ResolutionPhotoModal({ onSuccess }: ResolutionPhotoModalProps) {
  const queryClient = useQueryClient();
  const { currentMembership } = useAuthStore();
  const { resolutionPhoto, closeResolutionPhoto } = useModalStore();
  const companyId = currentMembership?.companyId;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpen = !!resolutionPhoto;
  const inspectionId = resolutionPhoto?.inspectionId;
  const findingId = resolutionPhoto?.findingId;

  // 폼 상태 (다중 사진)
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [notes, setNotes] = useState('');

  // UI 상태
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToastStore();

  // 모달 열릴 때 폼 리셋
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // 컴포넌트 언마운트 시 미리보기 URL 정리
  useEffect(() => {
    return () => {
      photos.forEach(p => URL.revokeObjectURL(p.previewUrl));
    };
  }, []);

  const resetForm = () => {
    photos.forEach(p => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setNotes('');
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    closeResolutionPhoto();
  };

  const validateFile = useCallback((file: File): boolean => {
    if (!file.type.startsWith('image/')) {
      showToast('이미지 파일만 업로드할 수 있습니다.', 'error');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('파일 크기가 10MB를 초과합니다.', 'error');
      return false;
    }
    return true;
  }, [showToast]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      showToast(`최대 ${MAX_PHOTOS}장까지 업로드할 수 있습니다.`, 'error');
      return;
    }
    const validFiles = fileArray.filter(validateFile).slice(0, remaining);
    const newPhotos: PhotoPreview[] = validFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, [photos.length, validateFile, showToast]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !inspectionId || !findingId) return;
    if (!notes.trim()) {
      showToast('조치사항을 입력해주세요.', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // 다중 사진 업로드
      const uploadedPhotos: Array<{ url: string; thumbnailUrl?: string; fileId?: string }> = [];

      for (const photo of photos) {
        const uploadResponse = await uploadResolutionPhoto(photo.file, companyId);
        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(uploadResponse.error?.message || '사진 업로드에 실패했습니다.');
        }
        uploadedPhotos.push({
          url: uploadResponse.data.url,
          fileId: uploadResponse.data.id,
        });
      }

      // 해결 처리
      const response = await resolveWithEvidence(
        inspectionId,
        findingId,
        {
          resolutionPhotoUrl: uploadedPhotos[0]?.url,
          resolutionPhotoId: uploadedPhotos[0]?.fileId,
          resolutionNotes: notes.trim() || undefined,
          resolutionPhotos: uploadedPhotos.length > 0 ? uploadedPhotos : undefined,
        },
        companyId
      );

      if (!response.success) {
        throw new Error(response.error?.message || '해결 처리에 실패했습니다.');
      }

      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });

      showToast('발견사항이 해결되었습니다.', 'success');
      onSuccess?.();
      handleClose();
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="발견사항 해결"
      description="해결 사진과 조치사항을 첨부하여 발견사항을 해결 처리합니다."
      size="lg"
      preventClose={isLoading}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          {/* 푸터는 base가 본문과 **형제**로 렌더하므로 아래 `<form>` 밖이다 — `form` 속성으로
              이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다). */}
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={isLoading || !notes.trim()}
          >
            {isLoading ? '처리 중...' : '해결 완료'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* 사진 업로드 (다중) */}
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              해결 사진 <span className="text-ink-400 font-normal">(선택)</span>
            </label>
            <p className="text-xs text-ink-500 mb-3">
              조치 완료된 상태의 사진을 첨부하세요. 중대 위험요인의 경우 사진 첨부를 권장합니다.
            </p>

            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative w-24 h-24 rounded-card-sm overflow-hidden border border-ink-200">
                    <img
                      src={photo.previewUrl}
                      alt={`해결 사진 ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {!isLoading && (
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(idx)}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600"
                        aria-label="사진 삭제"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}

                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    className="w-24 h-24 rounded-card-sm border-2 border-dashed border-ink-300 flex flex-col items-center justify-center text-ink-400 hover:border-brand-500 hover:text-brand-500 transition-colors disabled:opacity-50"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs mt-1">추가</span>
                  </button>
                )}
              </div>
            )}

            {photos.length === 0 && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`border-2 border-dashed rounded-card p-6 text-center transition-colors cursor-pointer ${
                  isLoading
                    ? 'border-ink-200 bg-ink-50 cursor-not-allowed'
                    : 'border-ink-300 hover:border-brand-500'
                }`}
                onClick={() => !isLoading && fileInputRef.current?.click()}
              >
                <UploadIcon className="mx-auto h-10 w-10 text-ink-400" />
                <p className="mt-2 text-sm text-ink-600">
                  클릭하거나 파일을 드래그하여 업로드
                </p>
                <p className="mt-1 text-xs text-ink-500">
                  PNG, JPG, HEIC 지원 (최대 10MB, {MAX_PHOTOS}장)
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading}
            />
          </div>

          {/* 조치사항 (필수) */}
          <div>
            <label htmlFor="resolution-notes" className="block text-sm font-medium text-ink-700">
              조치사항 <span className="text-red-500">*</span>
            </label>
            <textarea
              id="resolution-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={isLoading}
              required
              className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm focus:ring-brand-500 focus:border-brand-500 disabled:bg-ink-100"
              placeholder="조치 내용을 입력하세요..."
            />
          </div>
        </div>

        {/* 버튼 */}
      </form>
    </Modal>
  );
}

// Icons
function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
